/**
 * Marketing + analytics consent prompt API hooks (logged-in customers).
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import {
  syncAccountConsentPreferences,
  type AccountConsentPreferenceValues,
} from './accountConsentPreferencesSync.js';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import {
  fetchPlatformMarketingAnalyticsConsentPrompt,
  postPlatformMarketingAnalyticsConsentPrompt,
} from './platformConsentApi.js';
import { useConsentApiRolloutFallback } from './useConsentApiRolloutFallback.js';

export interface MarketingAnalyticsPromptData {
  needsPrompt: boolean;
  policyVersion: string;
  lastPromptedAt: string | null;
  deferAllowed: boolean;
  marketingGranted: boolean;
  analyticsGranted: boolean;
}

export function useMarketingAnalyticsConsentPrompt(): UseQueryResult<MarketingAnalyticsPromptData> {
  const config = useRuntimeConfig();
  const { session, sessionHydrated } = useCustomerSession();
  const rollout = useConsentApiRolloutFallback();

  return useQuery({
    queryKey: customerQueryKeys.marketingAnalyticsConsentPrompt('__platform__'),
    enabled: sessionHydrated && session !== null,
    queryFn: async ({ signal }): Promise<MarketingAnalyticsPromptData> =>
      fetchPlatformMarketingAnalyticsConsentPrompt({
        apiBaseUrl: config.apiBaseUrl,
        signal,
        rollout,
      }),
  });
}

export function useRecordMarketingAnalyticsPromptResponse(): {
  defer: () => Promise<void>;
  complete: () => Promise<void>;
} {
  const config = useRuntimeConfig();
  const rollout = useConsentApiRolloutFallback();
  const mutation = useMutation({
    mutationFn: async (
      response: 'COMPLETED' | 'DEFERRED_LEGAL' | 'DISMISSED_ILLEGAL',
    ): Promise<void> => {
      await postPlatformMarketingAnalyticsConsentPrompt({
        apiBaseUrl: config.apiBaseUrl,
        response,
        rollout,
      });
    },
  });

  return {
    defer: async (): Promise<void> => {
      await mutation.mutateAsync('DEFERRED_LEGAL');
    },
    complete: async (): Promise<void> => {
      await mutation.mutateAsync('COMPLETED');
    },
  };
}

export function useUpdateMarketingAnalyticsConsentPreferences(): {
  mutateAsync: (values: AccountConsentPreferenceValues) => Promise<void>;
  isPending: boolean;
} {
  const api = useApiClient();
  const tenantCode = '__platform__';
  const rollout = useConsentApiRolloutFallback();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (values: AccountConsentPreferenceValues): Promise<void> => {
      await syncAccountConsentPreferences(api, {
        tenantCode,
        authToken: null,
        values,
        rollout,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: customerQueryKeys.marketingAnalyticsConsentPrompt('__platform__'),
      });
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
